import React, { useEffect, useState, useRef, useContext } from "react";
import axios from "axios";
import { widget } from "../../Assets/charting_library/charting_library.min";
import * as Stomp from "stompjs";
import * as SockJS from "sockjs-client";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import InfoPopUp from "../../Common/InfoPopUp";
import { DataContext } from ".";
import { showToast } from "../../CoreDataService";
import InfoIcon from "@mui/icons-material/Info";
import ChartSlotHeader from "./MultiChart/ChartSlotHeader";
import { useTradingColors } from "./tradingColorConfig";

const title = "Chart Widget";
const description =
  "Displays trade chart for 1 minute,3 minutes, 5 minutes, 15 minutes, 30 minutes, 1 hour, 4 hours, 1 day, 2 days, 3 days, 1 week, 3 weeks, 1 month, 6 months, and years as well.";

const getTradingOverrides = (up, down) => ({
  "mainSeriesProperties.candleStyle.upColor": up,
  "mainSeriesProperties.candleStyle.downColor": down,
  "mainSeriesProperties.candleStyle.drawWick": true,
  "mainSeriesProperties.candleStyle.drawBorder": true,
  "mainSeriesProperties.candleStyle.borderColor": up,
  "mainSeriesProperties.candleStyle.borderUpColor": up,
  "mainSeriesProperties.candleStyle.borderDownColor": down,
  "mainSeriesProperties.candleStyle.wickUpColor": up,
  "mainSeriesProperties.candleStyle.wickDownColor": down,
  "mainSeriesProperties.barStyle.upColor": up,
  "mainSeriesProperties.barStyle.downColor": down,
  "mainSeriesProperties.hollowCandleStyle.upColor": up,
  "mainSeriesProperties.hollowCandleStyle.downColor": down,
  "mainSeriesProperties.hollowCandleStyle.borderColor": up,
  "mainSeriesProperties.hollowCandleStyle.borderUpColor": up,
  "mainSeriesProperties.hollowCandleStyle.borderDownColor": down,
  "mainSeriesProperties.hollowCandleStyle.wickUpColor": up,
  "mainSeriesProperties.hollowCandleStyle.wickDownColor": down,
  "mainSeriesProperties.haStyle.upColor": up,
  "mainSeriesProperties.haStyle.downColor": down,
  "mainSeriesProperties.haStyle.borderColor": up,
  "mainSeriesProperties.haStyle.borderUpColor": up,
  "mainSeriesProperties.haStyle.borderDownColor": down,
  "mainSeriesProperties.haStyle.wickUpColor": up,
  "mainSeriesProperties.haStyle.wickDownColor": down,
  "volume.volume.color.0": down,
  "volume.volume.color.1": up,
});

const getVolumeOverrides = (up, down) => ({
  "volume.volume.color.0": down,
  "volume.volume.color.1": up,
  "volume.volumePalette.0.color": down,
  "volume.volumePalette.1.color": up,
  "volume.volumePalette.colors.0.color": down,
  "volume.volumePalette.colors.1.color": up,
  "volume.Volume.color.0": down,
  "volume.Volume.color.1": up,
});

/**
 * Dynamically updates already-instantiated Volume indicator studies on the chart
 */
const updateVolumeStudyColors = (widgetInstance, up, down) => {
  if (!widgetInstance) return;

  // 1. Through TradingView Chart Public Study API
  try {
    if (typeof widgetInstance.chart === "function") {
      const chart = widgetInstance.chart();
      if (chart && typeof chart.getAllStudies === "function") {
        const allStudies = chart.getAllStudies() || [];
        allStudies.forEach((studyInfo) => {
          if (
            studyInfo &&
            studyInfo.name &&
            studyInfo.name.toLowerCase().includes("volume")
          ) {
            try {
              const studyApi = chart.getStudyById(studyInfo.id);
              if (studyApi && typeof studyApi.applyOverrides === "function") {
                studyApi.applyOverrides({
                  "Volume.color.0": down,
                  "Volume.color.1": up,
                  "Volume.volumePalette.0.color": down,
                  "Volume.volumePalette.1.color": up,
                  "Volume.volumePalette.colors.0.color": down,
                  "Volume.volumePalette.colors.1.color": up,
                  "vol.color.0": down,
                  "vol.color.1": up,
                  "volume.color.0": down,
                  "volume.color.1": up,
                  "palettes.volumePalette.colors.0.color": down,
                  "palettes.volumePalette.colors.1.color": up,
                });
              }

              // Direct property mutation on the study model instance
              const rawStudy = studyApi?._study;
              if (rawStudy && typeof rawStudy.properties === "function") {
                const props = rawStudy.properties();
                const palColors = props?.palettes?.volumePalette?.colors;
                if (palColors) {
                  if (palColors[0]?.color?.setValue) palColors[0].color.setValue(down);
                  if (palColors[1]?.color?.setValue) palColors[1].color.setValue(up);
                }
                if (typeof rawStudy.updateAllViews === "function") {
                  rawStudy.updateAllViews();
                }
              }
            } catch (studyErr) {
              console.warn("[TVChart] Error updating study:", studyErr);
            }
          }
        });
      }
    }
  } catch (e) {
    console.warn("[TVChart] Error updating chart studies:", e);
  }

  // 2. Direct model update through iframe innerWindow chartWidgetCollection
  try {
    const innerWin =
      typeof widgetInstance._innerWindow === "function"
        ? widgetInstance._innerWindow()
        : null;

    if (innerWin && innerWin.chartWidgetCollection) {
      const widgets =
        typeof innerWin.chartWidgetCollection.getAll === "function"
          ? innerWin.chartWidgetCollection.getAll()
          : [];

      widgets.forEach((w) => {
        try {
          const model = w?.model && typeof w.model === "function" ? w.model().model() : null;
          if (model && typeof model.allStudies === "function") {
            const allStudies = model.allStudies() || [];
            allStudies.forEach((study) => {
              const desc = study?.metaInfo && study.metaInfo()?.description;
              const shortId = study?.metaInfo && study.metaInfo()?.shortId;
              if (
                (desc && desc.toLowerCase().includes("volume")) ||
                (shortId && shortId.toLowerCase().includes("volume"))
              ) {
                const props = typeof study.properties === "function" ? study.properties() : null;
                const palColors = props?.palettes?.volumePalette?.colors;
                if (palColors) {
                  if (palColors[0]?.color?.setValue) palColors[0].color.setValue(down);
                  if (palColors[1]?.color?.setValue) palColors[1].color.setValue(up);
                }
                if (typeof study.updateAllViews === "function") {
                  study.updateAllViews();
                }
              }
            });
            if (typeof model.recalculateAllPanes === "function") {
              model.recalculateAllPanes();
            }
          }
        } catch (wErr) {
          console.warn("[TVChart] Error in inner widget update:", wErr);
        }
      });
    }
  } catch (innerErr) {
    console.warn("[TVChart] Error accessing innerWindow:", innerErr);
  }
};

const applyChartColors = (widgetInstance, up, down) => {
  if (!widgetInstance) return;
  try {
    if (typeof widgetInstance.applyOverrides === "function") {
      widgetInstance.applyOverrides(getTradingOverrides(up, down));
    }
    if (typeof widgetInstance.applyStudiesOverrides === "function") {
      widgetInstance.applyStudiesOverrides(getVolumeOverrides(up, down));
    }
    updateVolumeStudyColors(widgetInstance, up, down);
    setTimeout(() => {
      updateVolumeStudyColors(widgetInstance, up, down);
    }, 150);
    setTimeout(() => {
      updateVolumeStudyColors(widgetInstance, up, down);
    }, 400);
  } catch (err) {
    console.warn("[TVChart] Error applying color overrides:", err);
  }
};

const TvChartContainer = ({
  currencycode: propCurrencyCode,
  basecurrency: propBaseCurrency,
  instanceId = "main",
  height = "470px",
  containerId: propContainerId,
  showHeader = false,
  isActive = false,
  btnColor,
  onActivate,
  onSymbolChange,
  onClose,
  showClose = false,
}) => {
  const contextData = useContext(DataContext) || {};
  const currencycode = (propCurrencyCode || contextData.currencycode || "BTC").toUpperCase();
  const basecurrency = (propBaseCurrency || contextData.basecurrency || "USDT").toUpperCase();
  const isPublicView = contextData.isPublicView;
  const logo = localStorage.getItem("exchangeLogo");
  const { userInfo } = useSelector((state) => state);
  const { upColor, downColor } = useTradingColors();
  const [show, setShow] = useState(false);

  // Keep latest colors in ref for callbacks
  const colorsRef = useRef({ upColor, downColor });
  const isChartReadyRef = useRef(false);

  useEffect(() => {
    colorsRef.current = { upColor, downColor };
  }, [upColor, downColor]);

  // 🔹 INTERVAL FIX
  const intervalStorageKey = `tv_interval_spot_${instanceId}`;
  const [interval, setInterval] = useState(
    localStorage.getItem(intervalStorageKey) || "60"
  );

  const [datafeedUrl, setDatafeedUrl] = useState(
    "https://accounts.paybito.com/ChartApi/spot"
  );
  const [libraryPath, setLibraryPath] = useState("/charting_library/");
  const [chartsStorageUrl, setChartsStorageUrl] = useState(
    "https://saveload.tradingview.com"
  );
  const [chartsStorageApiVersion, setChartsStorageApiVersion] = useState("1.1");
  const [clientId, setClientId] = useState("tradingview.com");
  const [userId, setUserId] = useState(localStorage.getItem("uuid"));
  const [fullscreen, setFullscreen] = useState(false);
  const [autosize, setAutosize] = useState(true);
  const containerId = propContainerId || `tv_chart_container_${instanceId}`;
  const [tvWidget, setTvWidget] = useState(null);
  const [Themecolor, setThemecolor] = useState(
    isPublicView ? "dark" : (localStorage.getItem("theme") || "dark")
  );
  const [isLoading, setLoading] = useState(false);
  const handleClose = () => setShow(false);

  const websocketRef = useRef(null);
  const realtimeCallbackRef = useRef(null);
  const lastBarRef = useRef(null);

  const webSocketEndPoint = "https://stream.paybito.com/ChartStream/ws";
  const topic = "/user/topic/stream/tradingView";
  const reqNoRef = useRef(null);
  const intervalRef = useRef(interval);

  /* ---------------- SAVE / LOAD LAYOUT ---------------- */

  const saveChartState = (widget) => {
    if (!widget) return;
    const chartKey = `tvChart_${userId}_${instanceId}_${currencycode}/${basecurrency}`;
    widget.save((state) => {
      localStorage.setItem(chartKey, JSON.stringify(state));
      localStorage.setItem(`tvChart_${userId}_lastSaved`, chartKey);
      showToast("success", "Chart layout saved!");
    });
  };

  const loadChartState = (widget) => {
    if (!widget) return;
    const chartKey = `tvChart_${userId}_${instanceId}_${currencycode}/${basecurrency}`;
    const savedState = localStorage.getItem(chartKey);
    if (savedState) {
      try {
        widget.load(JSON.parse(savedState));
        // Re-apply active user trading colors so the saved layout doesn't override with old colors
        setTimeout(() => {
          applyChartColors(widget, colorsRef.current.upColor, colorsRef.current.downColor);
        }, 120);
      } catch (e) {
        console.error("Failed to load chart layout", e);
      }
    }
  };

  /* ---------------- WEBSOCKET ---------------- */

  const connectWebSocket = () => {
    if (!localStorage.getItem("access_token")) return;

    const socket = new SockJS(webSocketEndPoint);
    websocketRef.current = Stomp.over(socket);
    websocketRef.current.debug = null;

    websocketRef.current.connect({}, () => {
      websocketRef.current.subscribe(topic, onMessageReceived);
      subscribe();
    });
  };

  const disconnectWebSocket = () => {
    if (websocketRef.current?.connected) {
      websocketRef.current.disconnect();
    }
  };

  const sendMessage = (req) => {
    if (!websocketRef.current?.connected) return;
    websocketRef.current.send(
      "/app/sendRequest/tradingView",
      {},
      JSON.stringify(req)
    );
  };

  const mapResolution = (res) => {
    const resolutionMap = {
      "1": "1MIN", "3": "3MIN", "5": "5MIN", "15": "15MIN", "30": "30MIN",
      "60": "1HR", "240": "4HR",
    };
    return resolutionMap[res] || res;
  };

  const unsubscribe = () => {
    if (reqNoRef.current == null) return;
    sendMessage({
      method: "UNSUBSCRIBE",
      base: basecurrency,
      counter: currencycode,
      actualResolution: mapResolution(intervalRef.current),
      id: reqNoRef.current,
    });
  };

  const subscribe = (resolution) => {
    const res = resolution || intervalRef.current;
    reqNoRef.current = Math.floor(Math.random() * 1000) + 1;
    sendMessage({
      method: "SUBSCRIBE",
      base: basecurrency,
      counter: currencycode,
      actualResolution: mapResolution(res),
      id: reqNoRef.current,
    });
  };

  const onMessageReceived = (message) => {
    try {
      const parsed = JSON.parse(message.body);
      if (parsed?.t) updateRealtimeBar(parsed);
    } catch { }
  };

  const updateRealtimeBar = (data) => {
    if (!realtimeCallbackRef.current) return;

    const bar = {
      time: data.t[0] * 1000,
      open: Number(data.o[0]),
      high: Number(data.h[0]),
      low: Number(data.l[0]),
      close: Number(data.c[0]),
      volume: Number(data.v[0]) * 0.7,
    };

    if (lastBarRef.current?.time === bar.time) {
      bar.open = lastBarRef.current.open;
      bar.high = Math.max(bar.high, lastBarRef.current.high);
      bar.low = Math.min(bar.low, lastBarRef.current.low);
    }

    lastBarRef.current = bar;
    realtimeCallbackRef.current(bar);
  };

  /* ---------------- DATAFEED ---------------- */

  const createDatafeed = () => ({
    onReady: (cb) =>
      cb({
        supported_resolutions: [
          "1",
          "3",
          "5",
          "15",
          "30",
          "60",
          "240",
          "1D",
          "2D",
          "3D",
          "1W",
          "3W",
          "1M",
          "6M",
        ],
      }),

    resolveSymbol: (symbolName, onResolve) => {
      const split = symbolName.split(/[:/]/);
      onResolve({
        name: symbolName,
        description: `${split[1]}/${split[2]}`,
        type: "crypto",
        session: "24x7",
        timezone: "Etc/UTC",
        ticker: symbolName,
        exchange: split[0],
        minmov: 1,
        pricescale: 100,
        has_intraday: true,
        supported_resolutions: [
          "1",
          "3",
          "5",
          "15",
          "30",
          "60",
          "240",
          "1D",
          "2D",
          "3D",
          "1W",
          "3W",
          "1M",
          "6M",
        ],
        volume_precision: 8,
      });
    },

    getBars: async (symbolInfo, resolution, from, to, onHistory, onError) => {
      try {
        const response = await axios.get(
          `${datafeedUrl}/history?symbol=${currencycode}%2F${basecurrency}&resolution=${resolution}&from=${from}&to=${to}`
        );

        if (!response.data?.c?.length) {
          onHistory([], { noData: true });
          return;
        }

        const bars = response.data.c.map((c, i) => ({
          open: Number(response.data.o[i]),
          high: Number(response.data.h[i]),
          low: Number(response.data.l[i]),
          close: Number(c),
          volume: Number(response.data.v[i]) * 0.7,
          time: Number(response.data.t[i]) * 1000,
        }));

        bars.sort((a, b) => a.time - b.time);

        lastBarRef.current = bars[bars.length - 1];

        onHistory(bars, { noData: false });
      } catch (e) {
        console.error(e);
        onError(e);
      }
    },

    subscribeBars: (_, __, onRealtime) => {
      realtimeCallbackRef.current = onRealtime;
    },

    unsubscribeBars: () => {
      realtimeCallbackRef.current = null;
      lastBarRef.current = null;
    },
  });

  /* ---------------- INIT CHART ---------------- */

  useEffect(() => {
    setLoading(true);
    if (tvWidget) tvWidget.remove();

    const widgetOptions = {
      symbol: `:${currencycode}/${basecurrency}`,
      datafeed: createDatafeed(),
      interval: interval,
      container_id: containerId,
      library_path: libraryPath,
      locale: "en",
      disabled_features: [
        "header_compare",
        "header_symbol_search",
        "widget_logo",
      ],
      enabled_features: [
        "hide_left_toolbar_by_default",
        "header_indicators",
        "header_chart_type",
        "save_chart_properties_to_local_storage",
        "create_volume_indicator_by_default",
        "use_localstorage_for_settings",
        "header_saveload",
      ],
      client_id: clientId,
      user_id: userId,
      fullscreen,
      autosize,
      theme: Themecolor,
      overrides: getTradingOverrides(upColor, downColor),
      studies_overrides: getVolumeOverrides(upColor, downColor),
    };

    const newTvWidget = new widget(widgetOptions);

    newTvWidget.onChartReady(() => {
      isChartReadyRef.current = true;
      setTimeout(() => { window.dispatchEvent(new Event("resize")); }, 100);

      // 🔹 restore saved layout
      loadChartState(newTvWidget);

      // 🔹 Enforce active trading colors immediately when chart is ready
      const { upColor: curUp, downColor: curDown } = colorsRef.current;
      applyChartColors(newTvWidget, curUp, curDown);

      // Re-apply after short tick in case loadChartState asynchronously restored an old layout
      setTimeout(() => {
        applyChartColors(newTvWidget, colorsRef.current.upColor, colorsRef.current.downColor);
      }, 150);

      // 🔹 SAVE LAYOUT BUTTON (restored)
      newTvWidget.headerReady().then(() => {
        const saveButton = newTvWidget.createButton();
        saveButton.textContent = "Save Layout";
        saveButton.addEventListener("click", () => {
          saveChartState(newTvWidget);
        });
      });

      // 🔹 save interval on change (SAFE)
      newTvWidget
        .chart()
        .onIntervalChanged()
        .subscribe(null, (newInterval) => {
          unsubscribe();
          intervalRef.current = newInterval;
          localStorage.setItem(intervalStorageKey, newInterval);
          setInterval(newInterval);
          subscribe(newInterval);
        });

      setLoading(false);
    });

    setTvWidget(newTvWidget);
    disconnectWebSocket();
    connectWebSocket();

    return () => {
      isChartReadyRef.current = false;
      newTvWidget.remove();
      disconnectWebSocket();
    };
  }, [currencycode, basecurrency]);

  // Dynamically apply color preference overrides when user switches colors
  useEffect(() => {
    if (tvWidget && isChartReadyRef.current) {
      applyChartColors(tvWidget, upColor, downColor);
    }
  }, [upColor, downColor, tvWidget]);

  return (
    <div
      style={{
        position: "relative",
        height: "100%",
        width: "100%",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
      onClick={onActivate}
    >
      {showHeader && (
        <ChartSlotHeader
          currencycode={currencycode}
          basecurrency={basecurrency}
          interval={interval}
          onIntervalSelect={(newInt) => {
            if (tvWidget && tvWidget.chart) {
              try {
                tvWidget.chart().setResolution(newInt);
              } catch (e) {
                console.error("setResolution error", e);
              }
            }
          }}
          onSymbolSelect={(newCur, newBase) => {
            if (onSymbolChange) {
              onSymbolChange(newCur, newBase);
            }
          }}
          isActive={isActive}
          btnColor={btnColor}
          onActivate={onActivate}
          onClose={onClose}
          showClose={showClose}
        />
      )}
      <div
        id={containerId}
        style={{
          flex: 1,
          width: "100%",
          height: showHeader ? `calc(100% - 32px)` : "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      />
      <InfoPopUp
        open={show}
        onClose={handleClose}
        title={title}
        description={description}
      />
    </div>
  );
};

export default TvChartContainer;
